(() => {
  if (window.__midgardMarketPlugLoaded) {
    return;
  }

  window.__midgardMarketPlugLoaded = true;

  const pendingSelector = 'variant-picker[data-market-plug-pending]';
  const initializedPickers = new WeakSet();

  const getConfig = picker => {
    const configElement = picker.querySelector('[data-market-plug-config]');

    if (!configElement) {
      return null;
    }

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Market plug: invalid configuration.', error);
      return null;
    }
  };

  const getPurchaseSurfaces = (picker, formId) => {
    const surfaces = [];
    const productRerender = picker.closest('product-rerender');

    if (productRerender) {
      surfaces.push(...productRerender.querySelectorAll('buy-buttons'));
    }

    if (picker.getAttribute('context') === 'main_product') {
      const sticky = document.querySelector(
        `product-sticky-add-to-cart[main-product-form="${CSS.escape(formId)}"]`
      );

      if (sticky) {
        surfaces.push(sticky);
      }
    }

    return [...new Set(surfaces)];
  };

  const lockPurchases = (picker, formId) => {
    const surfaces = getPurchaseSurfaces(picker, formId);
    const previousStates = surfaces.map(surface => ({
      surface,
      inert: surface.inert,
      ariaBusy: surface.getAttribute('aria-busy')
    }));

    for (const surface of surfaces) {
      surface.inert = true;
      surface.setAttribute('aria-busy', 'true');
    }

    return () => {
      for (const state of previousStates) {
        if (!state.surface.isConnected) {
          continue;
        }

        state.surface.inert = state.inert;

        if (state.ariaBusy === null) {
          state.surface.removeAttribute('aria-busy');
        } else {
          state.surface.setAttribute('aria-busy', state.ariaBusy);
        }
      }
    };
  };

  const exposeManualSelection = (picker, message, detail) => {
    picker.removeAttribute('data-market-plug-pending');
    picker.removeAttribute('aria-busy');
    picker.setAttribute('data-market-plug-error', '');

    for (const fieldset of picker.querySelectorAll('.variant-picker__option')) {
      fieldset.hidden = false;
      fieldset.removeAttribute('aria-hidden');
    }

    console.error(message, detail || '');
  };

  const finishPicker = (picker, config, correctInput, plugInputs) => {
    correctInput.checked = true;

    const plugFieldset = correctInput.closest('.variant-picker__option');

    if (plugFieldset) {
      plugFieldset.hidden = true;
      plugFieldset.setAttribute('aria-hidden', 'true');
      plugFieldset.dataset.marketPlug = config.canonicalPlug;
    }

    for (const input of plugInputs) {
      if (input === correctInput) {
        continue;
      }

      input.dataset.marketPlugOptionPosition = input.dataset.optionPosition;
      input.removeAttribute('data-option-position');
    }

    picker.dataset.marketPlug = config.canonicalPlug;
    picker.removeAttribute('data-market-plug-pending');
    picker.removeAttribute('data-market-plug-error');
    picker.removeAttribute('aria-busy');
    picker.setAttribute('data-market-plug-ready', '');
  };

  const initializePicker = async picker => {
    if (initializedPickers.has(picker)) {
      return;
    }

    initializedPickers.add(picker);

    const config = getConfig(picker);
    const formId = picker.getAttribute('form-id');
    const productForm = formId ? document.getElementById(formId) : null;

    if (!config || !productForm) {
      exposeManualSelection(
        picker,
        'Market plug: configuration or product form is unavailable.',
        config
      );
      return;
    }

    const restorePurchases = lockPurchases(picker, formId);
    const optionInputs = Array.from(
      picker.querySelectorAll('input[data-option-position]')
    );
    const plugInputs = optionInputs.filter(
      input => Number(input.dataset.optionPosition) === Number(config.plugOptionPosition)
    );
    const correctInput = plugInputs.find(
      input => String(input.value) === String(config.plugOptionValueId)
    );

    if (!correctInput) {
      restorePurchases();
      exposeManualSelection(
        picker,
        'Market plug: the required market option is not selectable.',
        config
      );
      return;
    }

    if (correctInput.checked) {
      finishPicker(picker, config, correctInput, plugInputs);
      restorePurchases();
      return;
    }

    const selectedByPosition = new Map();

    for (const input of optionInputs) {
      const position = Number(input.dataset.optionPosition);

      if (position !== Number(config.plugOptionPosition) && input.checked) {
        selectedByPosition.set(position, input.value);
      }
    }

    selectedByPosition.set(
      Number(config.plugOptionPosition),
      config.plugOptionValueId
    );

    const optionValues = [];

    for (let position = 1; position <= Number(config.optionCount); position += 1) {
      if (!selectedByPosition.has(position)) {
        restorePurchases();
        exposeManualSelection(
          picker,
          'Market plug: the current option selection is incomplete.',
          selectedByPosition
        );
        return;
      }

      optionValues.push(selectedByPosition.get(position));
    }

    if (typeof picker.selectCombination !== 'function') {
      restorePurchases();
      exposeManualSelection(
        picker,
        'Market plug: the variant picker is unavailable.'
      );
      return;
    }

    try {
      await picker.selectCombination({
        optionValues,
        productChange: false
      });

      if (picker.isConnected) {
        finishPicker(picker, config, correctInput, plugInputs);
        restorePurchases();
      }
    } catch (error) {
      restorePurchases();
      exposeManualSelection(picker, 'Market plug: variant update failed.', error);
    }
  };

  const initializeWithin = root => {
    if (root.matches?.(pendingSelector)) {
      initializePicker(root);
    }

    for (const picker of root.querySelectorAll?.(pendingSelector) || []) {
      initializePicker(picker);
    }
  };

  const start = async () => {
    await window.customElements.whenDefined('variant-picker');
    initializeWithin(document);

    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            initializeWithin(node);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();